import { IChartStoreOptional } from './chartInterfaces';
import moment from 'moment';
import { makeAutoObservable } from 'mobx';

import {
    C_TIMELINE_MIN_DATE,
    C_TIMELINE_MAX_DATE,
    TIMELINE_OFFSET_START,
    TIMELINE_WINDOW_SIZE
} from '../../constants';

import { IRootStoreExporter, RiskMarkerData } from '../../interfaces';
import { RISK_CHART_MODES } from '../../services/APIService';
import { debounce } from 'lodash';

export const minRanges = {
    rangeLeft: 7,
    rangeRight: 7,
};

export const maxRanges = {
    minBaseDate: C_TIMELINE_MIN_DATE.clone(),
    maxBaseDate: C_TIMELINE_MAX_DATE.clone(),
};

export enum COMPOSITE_DATA_INTERVAL  {
    DAILY = 'daily',
    MONTHLY = 'monthly',
}

export class ChartStore implements IRootStoreExporter {
    public rangeLeft: number;
    public rangeRight: number;
    public baseDate: moment.Moment = moment().startOf('day');
    public showLines: boolean = true;
    //todo: depracate prop
    public showCircles: boolean = false;
    public showVirtualToday: boolean = true;
    public opacity: boolean = false;
    public virtualToday: moment.Moment = moment().startOf('day');
    public width: number = 800;
    public height: number = 90;
    public clientWidth: number = 800;
    public clientHeight: number = 0;
    public sequence: number = 0;
    public isDragging: boolean = false;
    public compositeDataInterval: COMPOSITE_DATA_INTERVAL = COMPOSITE_DATA_INTERVAL.DAILY;
    // Snapshot of risk-chart selections taken when switching into Monthly mode, so that
    // Pulse / Past Projections selections (which are force-disabled in Monthly because the
    // backend doesn't return that data for monthly resolution) can be restored when the
    // user switches back to Daily.
    public preMonthlyRiskChartMode: RISK_CHART_MODES | null = null;
    public preMonthlyProjectionDataOn: boolean | null = null;
    public riskMarkerEvent: RiskMarkerData = null;

    public updateDimensions = () => {
        const elem = document.querySelector('.gq-chart');
        if (!elem) {
            return;
        }
        const { width, height } = elem.getBoundingClientRect();
        if (this.width !== width || this.height !== height) {
            this.setState({
                width,
                height,
            });
        }
    };
    public setState(state: IChartStoreOptional) {
        Object.assign(this, state);
    }

    constructor() {
        makeAutoObservable(this);
        this.updateStateByScoresDelta(TIMELINE_OFFSET_START, TIMELINE_WINDOW_SIZE);
    }
    public updateStateByScoresDelta(
        scoresDeltaOffsetStart,
        scoresDeltaWindowSize
    ) {
        let baseDate = moment().startOf('day');
        const startDate = baseDate.clone().add(scoresDeltaOffsetStart, 'days');
        const endDate = startDate.clone().add(scoresDeltaWindowSize, 'days');
        this.setState({
            rangeLeft: baseDate.diff(startDate, 'days'),
            rangeRight: endDate.diff(baseDate, 'days'),
            baseDate,
            showLines: true,
            showCircles: false,
            opacity: false,
            virtualToday: baseDate,
            isDragging: false,
            showVirtualToday: true,
            clientWidth: document.documentElement.clientWidth,
            clientHeight: document.documentElement.clientHeight,
        });
    }

    // ** NOTICE ** consider using the newer setNewRangeByDates method, since this one is probably gonna be deprecated
    public setNewRanges(from: number, until: number) {
        // Minimum total gap (in days) between the two edges of the picker.
        const MIN_GAP = minRanges.rangeLeft + minRanges.rangeRight;
        // Detect which edge the caller actually moved so we only ever clamp
        // the moved edge — never drag the stationary one along with it.
        const leftChanged = from !== this.rangeLeft;
        let newLeft = from;
        let newRight = until;
        if (newLeft + newRight < MIN_GAP) {
            if (leftChanged) {
                newLeft = MIN_GAP - newRight;
            } else {
                newRight = MIN_GAP - newLeft;
            }
        }
        let xLeft = this.baseDate.clone().subtract(newLeft, 'days');
        let xRight = this.baseDate.clone().add(newRight, 'days');
        if (xLeft.isBefore(maxRanges.minBaseDate)) {
            xLeft = maxRanges.minBaseDate;
            newLeft = this.baseDate.diff(xLeft, 'days');
        }

        if (xRight.isAfter(maxRanges.maxBaseDate)) {
            xRight = maxRanges.maxBaseDate;
            newRight = xRight.diff(this.baseDate, 'days');
        }
        this.setState({
            rangeLeft: newLeft,
            rangeRight: newRight,
        });
    }

    public setNewRangeByDates = (leftDate: Date, rightDate: Date) => {
        const startDate = moment.max(moment(leftDate), maxRanges.minBaseDate);
        const endDate = moment.min(moment(rightDate), maxRanges.maxBaseDate);
        const daysDiff = endDate.diff(startDate, 'days');
        if (daysDiff < 1) {
            throw new Error(
                'Invalid date range given. rightDate must be at least one day bigger than leftDate'
            );
        }

        const baseDate = this.ensureDateWithinRange(
            this.baseDate,
            startDate,
            endDate
        );
        const virtualToday = this.getVirtualTodayAccordingToSelectedDateRange(this.virtualToday, startDate, endDate);
        this.setState({
            baseDate,
            virtualToday,
            rangeLeft: baseDate.diff(startDate, 'days'),
            rangeRight: endDate.diff(baseDate, 'days'),
        });
    };

    public increaseSequence() {
        this.sequence++;
    }

    public setVirtualToday(moment: moment.Moment) {
        this.virtualToday = moment;
    }

    public setNewBaseDate(
        date: moment.Moment,
        virtualToday: moment.Moment
    ): moment.Moment | undefined {
        if (date.diff(this.baseDate, 'days') === 0) {
            return;
        }
        const possibleMinimum = date.clone().subtract(this.rangeLeft, 'days');
        const possibleMaximum = date.clone().add(this.rangeRight, 'days');
        if (
            possibleMinimum.isAfter(maxRanges.minBaseDate, 'days') &&
            possibleMaximum.isBefore(maxRanges.maxBaseDate, 'days')
        ) {
            this.setState({
                baseDate: date,
            });

            if (virtualToday.isAfter(possibleMaximum, 'days')) {
                return possibleMaximum;
            }

            if (virtualToday.isBefore(possibleMinimum, 'days')) {
                return possibleMinimum;
            }
        }
    }

    public exportKeys() {
        return [
            'rangeLeft',
            'rangeRight',
            'baseDate',
            'showLines',
            'showCircles',
            'opacity',
            'virtualToday',
        ];
    }

    get rangeLeftDate() {
        return this.baseDate.clone().subtract(this.rangeLeft, 'days');
    }

    get rangeRightDate() {
        return this.baseDate.clone().add(this.rangeRight, 'days');
    }

    get virtualTodayRangeStart() {
        const start = this.virtualToday.clone().subtract(this.rangeLeft, 'days');
        return moment.max(start, maxRanges.minBaseDate).clone();
    }

    get virtualTodayRangeEnd() {
        const end = this.virtualToday.clone().add(this.rangeRight, 'days');
        return moment.min(end, maxRanges.maxBaseDate).clone();
    }

    get minPossibleRangeLeftDate() {
        // latest the left edge may go = just before the current right edge
        return this.baseDate
            .clone()
            .add(this.rangeRight, 'days')
            .subtract(minRanges.rangeLeft, 'days');
    }

    get minPossibleRangeRightDate() {
        // earliest the right edge may go = just after the current left edge
        return this.baseDate
            .clone()
            .subtract(this.rangeLeft, 'days')
            .add(minRanges.rangeRight, 'days');
    }

    private ensureDateWithinRange(
        date: moment.Moment,
        rangeStart: moment.Moment,
        rangeEnd: moment.Moment
    ) {
        if (date.isBefore(rangeStart) || this.baseDate.isAfter(rangeEnd)) {
            const daysDiff = rangeEnd.diff(rangeStart, 'days');
            return rangeStart.clone().add(Math.round(daysDiff / 2), 'days');
        } else {
            return date;
        }
    }

    private getVirtualTodayAccordingToSelectedDateRange(currentVirtualToday: moment.Moment, newRangeStart: moment.Moment, newRangeEnd: moment.Moment) {
        // Current value of VirtualToday is within the newly selected start & end dates - do nothing.
        if(currentVirtualToday.isSameOrAfter(newRangeStart) && currentVirtualToday.isSameOrBefore(newRangeEnd)){
            return currentVirtualToday;
        }
        const today = moment().startOf('day');
        // Selected range is in the past:  VirtualToday should be set to the end date. 
        if(newRangeStart.isSameOrBefore(today) && newRangeEnd.isSameOrBefore(today)){
            return newRangeEnd.clone()
        }
        // Selected range is in the future: VirtualToday should be set to the start date.
        if(newRangeStart.isSameOrAfter(today) && newRangeEnd.isSameOrAfter(today)){
            return newRangeStart.clone()
        }

        // Start date is in the past and end date is in the future - VirtualToday should be set to today.
        if(newRangeStart.isSameOrBefore(today) && newRangeEnd.isSameOrAfter(today)){
            return today.clone()
        }
    }
}

const chartStore = new ChartStore();

const onResize = () => {
    const elem = document.querySelector('.gq-chart');
    if (!elem) {
        return;
    }

    const width = elem.clientWidth;
    const height = elem.clientHeight;
    const { clientWidth, clientHeight } = document.documentElement;

    if (
        chartStore.clientWidth === clientWidth &&
        chartStore.clientHeight === clientHeight &&
        width === chartStore.width &&
        height === chartStore.height
    ) {
        return;
    }
    chartStore.setState({
        // todo: refactor elem.getWidth/Height
        width: elem.clientWidth,
        height: elem.clientHeight,
        clientWidth,
        clientHeight,
    });
};
const onResizeDebounced = debounce(onResize, 300);

window.addEventListener('resize', onResizeDebounced);

export default chartStore;
